import React from "react";

/**
 * Top-level error boundary.
 *
 * Why this exists:
 *   Some users (especially those running a stale `index.html` cached
 *   at Cloudflare's edge) can land on a JS bundle that references
 *   identifiers that don't exist in the live HTML anymore. When that
 *   happens, React throws during render and the whole tree unmounts,
 *   leaving the user staring at a *blank white page*. That's exactly
 *   the "All Jobs goes blank for CS users" symptom the team has been
 *   chasing.
 *
 * What it does:
 *   1. Catches any render-time exception below it in the tree.
 *   2. Logs the error to the console (so we can grab it from
 *      DevTools / Sentry later).
 *   3. Shows a friendly recovery panel with a "Reload latest version"
 *      button that hard-reloads the tab with a `?v=<now>` cache-buster.
 *      That query-string changes Cloudflare's edge cache key, so the
 *      browser is guaranteed to receive a *fresh* `index.html` from
 *      origin on the next request — which usually fixes the crash
 *      because the JS and HTML are back in sync.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Surface in DevTools and any attached reporter. Don't swallow.
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary] Render crash:", error, info);
  }

  handleReload = () => {
    // Force a cache-busted reload. Identical pattern to versionCheck.js
    // so we know it bypasses Cloudflare's edge cache.
    try {
      const url = new URL(window.location.href);
      url.searchParams.set("v", String(Date.now()));
      // Drop sso_token so we don't replay a single-use token on reload.
      url.searchParams.delete("sso_token");
      window.location.replace(url.toString());
    } catch {
      window.location.reload();
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    const msg = this.state.error?.message || "Unexpected error";
    return (
      <div
        data-testid="error-boundary-screen"
        className="min-h-screen flex items-center justify-center bg-slate-50 p-6"
      >
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-2xl shadow-sm p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center font-bold">
              !
            </div>
            <div>
              <h1 className="font-heading font-semibold text-slate-900 text-lg">
                Something went wrong
              </h1>
              <p className="text-xs text-slate-500">
                A page render error occurred — usually due to a stale cached
                version after a recent deploy.
              </p>
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[11px] font-mono text-slate-600 break-all">
            {msg}
          </div>
          <p className="text-xs text-slate-500">
            Click the button below to fetch the latest version. This bypasses
            Cloudflare's cache and reloads cleanly.
          </p>
          <button
            data-testid="error-boundary-reload-btn"
            onClick={this.handleReload}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-xl py-2.5 transition-colors"
          >
            Reload latest version
          </button>
          <button
            data-testid="error-boundary-signout-btn"
            onClick={() => {
              try {
                document.cookie = "sjc_session=; Max-Age=0; Path=/";
              } catch { /* ignore */ }
              try { localStorage.removeItem("cargodash_session"); } catch { /* ignore */ }
              window.location.href = "/login?v=" + Date.now();
            }}
            className="w-full text-slate-500 hover:text-slate-700 text-xs"
          >
            Or sign out and try again
          </button>
        </div>
      </div>
    );
  }
}
